import { usePrivateToast } from "@/shared/usePrivateToast";
import { usePrivateApi } from "@/shared/usePrivateApi";
import { countedKey } from "@/lib/i18n/plural";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmModal, EmptyState, LoadingSkeleton } from "@a2z/ui";

import { endpoints } from "@/lib/api/endpoints";
import { useI18n, useTranslate } from "@/lib/i18n";
import { queryKeys, useApiQuery } from "@/shared/queries";
import { useErrorMessage } from "@/shared/errors";
import { PERMISSIONS, hasPermission, useSession } from "@/modules/auth/useSession";
import {
  LOCKED_SECTION_TYPES,
  PREVIEW_ONLY_SECTION_TYPES,
  sectionLabel,
} from "@/modules/manage/lib/sectionLabels";
import {
  canMove,
  isVisible,
  moveSection,
  toggleSection,
} from "@/modules/manage/lib/sectionOrder";
import {
  copyFieldsFor,
  readCopy,
  toggleFieldsFor,
  writeCopy,
  writeToggle,
} from "@/modules/manage/lib/sectionCopy";
import { readColors, type ThemeColors } from "@/modules/manage/lib/themeColors";
import { THEME_CARDS } from "@/modules/manage/lib/themeCards";
import { ThemeColorsEditor } from "@/modules/manage/components/ThemeColorsEditor";
import {
  StorefrontPreview,
  type PreviewDevice,
} from "@/modules/manage/components/StorefrontPreview";
import { DeviceFrame } from "@/modules/manage/components/editor/DeviceFrame";
import {
  EDITOR_VARS,
  EyeIcon,
  Field,
  GroupLabel,
  EditorIconButton,
  PillToggle,
} from "@/modules/manage/components/editor/editorChrome";
import type {
  StoreThemeView,
  ThemeGalleryItem,
  ThemeSectionInstance,
} from "@/lib/api/types";

type Tab = "theme" | "content" | "sections";

/**
 * The storefront editor — `Trioty Restaurant Editor.dc.html`.
 *
 * A 52px bar, a 290px rail of three tabs, and the seller's real storefront in a
 * device frame. Every number is the artboard's; `editorChrome` carries the
 * palette and says why the editor keeps one apart from the rest of the portal.
 *
 * ## The canvas is the REAL page
 *
 * The artboard draws a simulated storefront, because an artboard has to. We
 * have the actual one: the buyer app rendering this store's draft behind a
 * preview token, inside the artboard's browser window or handset. Anything
 * simulated here would be a second implementation of six skins — and the one
 * that drifts.
 *
 * ## Three tabs, because a restaurant makes three decisions
 *
 * Which design (and its colours), what it says, and what shows. There is no
 * fourth tab for structure: the six designs are authored compositions whose
 * whole value is that they are coherent (D15), so a seller reorders and hides,
 * never rebuilds.
 */
export function StorefrontEditorPage() {
  const { apiFetch } = usePrivateApi();
  const toast = usePrivateToast();
  const t = useTranslate();
  const { locale } = useI18n();
  const params = useParams();
  const queryClient = useQueryClient();
  const errorMessage = useErrorMessage();
  const { data: session } = useSession();

  const [tab, setTab] = useState<Tab>("theme");
  const [device, setDevice] = useState<PreviewDevice>("desktop");
  const [busy, setBusy] = useState<string | null>(null);
  const [previewVersion, setPreviewVersion] = useState(0);
  /**
   * Bumped when the draft is replaced from OUTSIDE the copy boxes — a theme
   * applied, a reset, an undo, a publish.
   *
   * Separate from `previewVersion` on purpose. Both reload the canvas, but only
   * these should reseed the inputs: a reload caused by the seller's own typing
   * (a headline the page cannot patch in place) must leave the box they are
   * typing in exactly as it is, or the caret jumps to the end mid-word — the
   * very bug the stable key exists to prevent.
   */
  const [reseed, setReseed] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [livePatch, setLivePatch] = useState<{ draft: unknown; at: number } | null>(
    null,
  );
  /* A counter, not a clock: `Date.now()` in a component body is impure, and all
     this value has to do is differ from the last one. */
  const patchSeq = useRef(0);
  /**
   * Reload the canvas, and DROP any live patch first.
   *
   * `livePatch` is re-posted into every frame that reports ready, so that a
   * colour survives a reload it did not cause. That is right for a colour and
   * wrong for everything else: after applying a different theme it meant the
   * previous theme's palette was pushed straight back over the new one — the
   * rail and the browser chrome showed Homegrill's yellow while the page
   * rendered Stack's red, because the last thing posted into the frame was
   * Stack's draft. A reload starts from what the server just sent.
   */
  const reloadPreview = () => {
    /*
     * Cancel anything queued FIRST.
     *
     * A reload means the draft just changed underneath the editor — a different
     * theme was applied, an undo landed, a publish went out. A write queued
     * before that was built from the previous draft, and letting its 800ms
     * timer fire afterwards puts the OLD theme's templates back over the new
     * one: the seller switches theme, types a word, and watches the previous
     * theme's text reappear before the next render corrects it.
     */
    if (flushTimer.current) clearTimeout(flushTimer.current);
    pending.current = null;
    setDirty(false);
    setLivePatch(null);
    setPreviewVersion((version) => version + 1);
  };
  /*
   * The pending write and its timer.
   *
   * There is no save button anywhere in the artboard, so a change has to
   * persist itself. It cannot persist on every keystroke either: each write is
   * a whole-draft PATCH, and the ones that touch markup also reload the canvas.
   * So the newest draft is held here and flushed once the seller pauses — the
   * artboard's own `dirty()` does exactly this with a 900ms wait.
   */
  const pending = useRef<{
    next: { settings: unknown; templates: unknown; pages?: unknown };
    remount: boolean;
  } | null>(null);
  const flushTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * The page could not paint one of the fields just written.
   *
   * Some values are not printed as a single string — STACK's hero splits the
   * headline a word to a line and colours the bracketed one — and writing text
   * over that markup flattens the design until the next server render puts it
   * back. The bridge refuses to do it and names the fields instead, so the
   * write becomes a reload: double-buffered, and therefore invisible.
   *
   * The queued write is upgraded in place rather than re-sent, because it is
   * the SAME edit — it just needs the other delivery. Its timer keeps running,
   * so a seller who is still typing still gets one reload when they stop.
   */
  /* A plain function, not a `useCallback`: memoising it would capture the
     pending-write ref in a hook, which the compiler rightly refuses to let this
     mutate. Re-subscribing a message listener per render costs nothing. */
  const handleUnpatched = () => {
    if (pending.current && !pending.current.remount) {
      pending.current.remount = true;
      // Nothing more will be posted into the frame for this edit, so drop the
      // patch: it is re-posted to every frame that reports ready, and a stale
      // one would land on the reload it just triggered.
      setLivePatch(null);
    }
  };
  const [dirty, setDirty] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  /*
   * What the previewed page says about its own controls: which are on screen at
   * this width, and which it tagged at all. `null` until the first answer, which
   * is the difference between "nothing is visible" and "we have not asked yet".
   *
   * Filtering happens only WITHIN `known`. A field no skin has tagged is not a
   * field the page is hiding — it is one nobody has marked up — and dropping it
   * would remove a control that works.
   */
  const [fieldReport, setFieldReport] = useState<{
    visible: Set<string>;
    known: Set<string>;
  } | null>(null);
  const [history, setHistory] = useState<
    { settings: unknown; templates: unknown; pages?: unknown }[]
  >([]);

  const canManage = hasPermission(session, PERMISSIONS.SETTINGS_MANAGE);

  const theme = useApiQuery<StoreThemeView>(
    queryKeys.storeTheme,
    endpoints.seller.theme,
    { enabled: canManage },
  );
  const gallery = useApiQuery<ThemeGalleryItem[]>(
    queryKeys.themeGallery,
    endpoints.seller.themes,
    { enabled: canManage },
  );
  const preview = useQuery({
    queryKey: queryKeys.storeThemePreviewToken,
    // A POST, so it cannot ride `useApiQuery` — that helper only ever GETs.
    queryFn: () =>
      apiFetch<{ token: string; expiresAt: string }>(
        endpoints.seller.themePreviewToken,
        { method: "POST", body: {} },
      ),
    enabled: canManage,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!canManage) {
    return (
      <div className="p-6">
        <EmptyState
          title={t((m) => m.manage.settingsNoAccessTitle)}
          description={t((m) => m.manage.settingsNoAccessBody)}
        />
      </div>
    );
  }

  const current = theme.data?.draft ?? theme.data?.live ?? null;
  const sections: ThemeSectionInstance[] = current?.templates?.home?.sections ?? [];
  const storeSlug = session?.seller.storeSlug ?? "";
  const storeName = session?.seller.storeName ?? storeSlug;
  const themeColors = readColors(current?.settings);
  const layout =
    (current?.settings as { layout?: string } | undefined)?.layout ?? "stack";
  const identityOf = (family: string) => THEME_CARDS[family];
  const appliedId = theme.data?.appliedThemeId ?? null;
  const hasDraft = theme.data?.hasDraft ?? false;
  const shownCount = sections.filter(isVisible).length;
  const contentLocale: "ar" | "en" = locale === "ar" ? "ar" : "en";

  const nameOf = (item: ThemeGalleryItem) =>
    locale === "ar" ? item.nameAr : item.nameEn || item.nameAr;
  const bodyOf = (item: ThemeGalleryItem) =>
    (locale === "ar" ? item.descriptionAr : item.descriptionEn) ?? "";

  // ── writes ────────────────────────────────────────────────────────────────

  const flush = async () => {
    const queued = pending.current;
    pending.current = null;
    if (!queued || !current) {
      setDirty(false);
      return;
    }
    try {
      await apiFetch(endpoints.seller.themeDraft, {
        method: "PATCH",
        body: {
          schemaVersion: current.schemaVersion,
          settings: queued.next.settings,
          templates: queued.next.templates,
          ...(queued.next.pages ? { pages: queued.next.pages } : {}),
        },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeTheme });
      if (queued.remount) reloadPreview();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setDirty(false);
    }
  };

  /**
   * Take a change now, persist it shortly.
   *
   * `remount: false` also pushes the draft straight into the live frame, so a
   * colour lands before the request does — which is the whole point: a seller
   * drags a swatch and the storefront follows, rather than following a save.
   */
  const schedule = (
    next: { settings: unknown; templates: unknown; pages?: unknown },
    { remount }: { remount: boolean },
  ) => {
    if (!current) return;
    // Snapshot only the FIRST change of a burst, so one undo steps back over a
    // whole edit rather than over each keystroke inside it.
    if (!pending.current) {
      setHistory((stack) => [
        ...stack.slice(-19),
        {
          settings: current.settings,
          templates: current.templates,
          ...(current.pages ? { pages: current.pages } : {}),
        },
      ]);
    }
    pending.current = { next, remount };
    setDirty(true);
    if (!remount) {
      patchSeq.current += 1;
      setLivePatch({ draft: next, at: patchSeq.current });
    }
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(() => void flush(), 800);
  };

  const guard = async (key: string, run: () => Promise<void>) => {
    setBusy(key);
    try {
      await run();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(null);
    }
  };

  const writeSections = (
    next: ThemeSectionInstance[],
    /**
     * COPY changes patch in place: the skins tag the element that renders each
     * setting, so the bridge writes the new words straight into the page and
     * the seller sees them land under the cursor. Everything else — visibility,
     * order, a different theme — changes what the page IS, and only the server
     * can render that.
     */
    { remount }: { remount: boolean } = { remount: true },
  ) =>
    schedule(
      {
        settings: current?.settings,
        templates: {
          ...current?.templates,
          home: { ...current?.templates.home, sections: next },
        },
        ...(current?.pages ? { pages: current.pages } : {}),
      },
      { remount },
    );

  const writeColors = (colors: ThemeColors) =>
    schedule(
      {
        settings: { ...(current?.settings as object), colors },
        templates: current?.templates,
        ...(current?.pages ? { pages: current.pages } : {}),
      },
      // No reload: the bridge repaints the palette in place.
      { remount: false },
    );

  const applyTheme = (item: ThemeGalleryItem) =>
    void guard(item.id, async () => {
      await apiFetch(endpoints.seller.themeApply(item.id), {
        method: "POST",
        body: {},
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeTheme });
      reloadPreview();
      // The draft was replaced, not edited — reseed the copy boxes.
      setReseed((n) => n + 1);
    });

  /**
   * Put the theme back exactly as the catalog ships it.
   *
   * Re-APPLIES the already-applied theme, which the service copies verbatim
   * into the draft — the same path that first put it there, and the same one
   * the demo storefronts are built from. That is why this is the honest way
   * back and a client-side "restore defaults" is not: the authored composition
   * lives in the catalog, and only the server has it.
   *
   * Draft only. The published page is untouched until the seller publishes.
   */
  const resetToTheme = () =>
    void guard("reset", async () => {
      if (!appliedId) return;
      await apiFetch(endpoints.seller.themeApply(appliedId), {
        method: "POST",
        body: {},
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeTheme });
      setHistory([]);
      reloadPreview();
      // The draft was replaced, not edited — reseed the copy boxes.
      setReseed((n) => n + 1);
      setResetOpen(false);
      toast.success(t((m) => m.manage.storefrontResetDone));
    });

  const publish = () =>
    void guard("publish", async () => {
      await apiFetch(endpoints.seller.themePublish, { method: "POST", body: {} });
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeTheme });
      reloadPreview();
      // The draft was replaced, not edited — reseed the copy boxes.
      setReseed((n) => n + 1);
      toast.success(t((m) => m.manage.storefrontPublished));
    });

  const undo = () =>
    void guard("undo", async () => {
      const previous = history[history.length - 1];
      if (!previous || !current) return;
      await apiFetch(endpoints.seller.themeDraft, {
        method: "PATCH",
        body: { schemaVersion: current.schemaVersion, ...previous },
      });
      // Pop AFTER the write lands — a failed undo must not lose the step.
      setHistory((stack) => stack.slice(0, -1));
      await queryClient.invalidateQueries({ queryKey: queryKeys.storeTheme });
      reloadPreview();
      // The draft was replaced, not edited — reseed the copy boxes.
      setReseed((n) => n + 1);
    });

  // ── chrome ────────────────────────────────────────────────────────────────

  const tabs: Array<[Tab, string]> = [
    ["theme", t((m) => m.manage.storefrontTabTheme)],
    ["content", t((m) => m.manage.storefrontTabContent)],
    ["sections", t((m) => m.manage.storefrontTabSections)],
  ];

  const deviceButton = (option: PreviewDevice, label: string, glyph: ReactNode) => {
    const on = device === option;
    return (
      <button
        type="button"
        aria-label={label}
        aria-pressed={on}
        onClick={() => setDevice(option)}
        data-testid={`storefront-device-${option}`}
        className="tri-ib"
        style={{
          width: 34,
          height: 28,
          borderRadius: 7,
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: on ? "var(--card)" : "transparent",
          color: on ? "var(--ink)" : "var(--ink3)",
          boxShadow: on ? "0 1px 3px rgba(0,0,0,.12)" : "none",
        }}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {glyph}
        </svg>
      </button>
    );
  };

  return (
    <div
      className="tri-root"
      style={{
        /* `100svh`, not `vh`: a mobile browser's collapsing chrome makes `vh`
           taller than the visible page, which would put the publish button
           under the address bar. Hard rule in this app. Less the
           live-updates warning's height, which sits in flow above it. */
        height: "calc(100svh - var(--realtime-notice-h, 0px))",
        background: "var(--app-bg)",
        color: "var(--ink)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{EDITOR_VARS}</style>

      {/* ── TOP BAR ──────────────────────────────────────────────────────── */}
      <div
        style={{
          flexShrink: 0,
          height: 52,
          background: "var(--shell)",
          borderBottom: "1px solid var(--shell-br)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 14px",
        }}
      >
        <Link
          to={`/${params.locale ?? locale}/manage/settings/online`}
          aria-label={t((m) => m.manage.storefrontEditorBack)}
          className="tri-ib"
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            border: "1px solid var(--card-br)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--ink2)",
            flexShrink: 0,
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {/* Mirrored in Arabic — a back arrow points the way the reader came. */}
            <path
              d={
                locale === "ar" ? "M5 12h14M12 19l7-7-7-7" : "M19 12H5M12 19l-7-7 7-7"
              }
            />
          </svg>
        </Link>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: "-.01em" }}>
            {storeName}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ink3)",
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "var(--ok-fg)",
              }}
            />
            {t((m) => m.manage.storefrontEditorSubtitle)} · {layout.toUpperCase()}
          </div>
        </div>

        <span style={{ flex: 1 }} />

        <div
          style={{
            display: "flex",
            gap: 2,
            background: "var(--pill)",
            border: "1px solid var(--pillring)",
            borderRadius: 9,
            padding: 2,
          }}
        >
          {deviceButton(
            "desktop",
            t((m) => m.manage.storefrontPreviewDesktop),
            <>
              <rect x="2" y="4" width="20" height="13" rx="2" />
              <path d="M8 21h8M12 17v4" />
            </>,
          )}
          {deviceButton(
            "phone",
            t((m) => m.manage.storefrontPreviewPhone),
            <>
              <rect x="7" y="2" width="10" height="20" rx="2.5" />
              <path d="M11 18h2" />
            </>,
          )}
        </div>

        <EditorIconButton
          label={t((m) => m.manage.storefrontUndo)}
          onClick={undo}
          disabled={history.length === 0 || busy !== null}
          size={28}
          testId="storefront-editor-undo"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 14 4 9l5-5" />
            <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
          </svg>
        </EditorIconButton>

        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11,
            fontWeight: 700,
            color: "var(--ink3)",
            whiteSpace: "nowrap",
          }}
        >
          {busy === null && !dirty ? (
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ok-fg)"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
          ) : null}
          {busy === null && !dirty
            ? t((m) => m.manage.storefrontSaved)
            : t((m) => m.manage.storefrontSaving)}
        </span>

        <button
          type="button"
          onClick={publish}
          disabled={!hasDraft || busy !== null}
          data-testid="storefront-editor-publish"
          className="ed-publish"
          style={{
            height: 32,
            padding: "0 16px",
            borderRadius: 9,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            border: "none",
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          {t((m) => m.manage.storefrontPublish)}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        {/* ── SIDEBAR ────────────────────────────────────────────────────── */}
        <div
          style={{
            flexShrink: 0,
            width: 290,
            background: "var(--shell)",
            borderInlineEnd: "1px solid var(--shell-br)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            role="tablist"
            style={{
              flexShrink: 0,
              display: "flex",
              gap: 2,
              background: "var(--pill)",
              border: "1px solid var(--pillring)",
              borderRadius: 10,
              padding: 3,
              margin: "12px 12px 0",
            }}
          >
            {tabs.map(([id, label]) => {
              const on = tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(id)}
                  data-testid={`storefront-tab-${id}`}
                  className="tri-ib"
                  style={{
                    flex: 1,
                    padding: "7px 4px",
                    borderRadius: 7,
                    border: "none",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 11,
                    fontWeight: 800,
                    background: on ? "var(--card)" : "transparent",
                    color: on ? "var(--ink)" : "var(--ink3)",
                    boxShadow: on ? "0 1px 3px rgba(0,0,0,.12)" : "none",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 12 }}>
            {theme.isLoading ? <LoadingSkeleton rows={5} /> : null}

            {!theme.isLoading && tab === "theme" ? (
              <>
                <GroupLabel>{t((m) => m.manage.storefrontGroupTheme)}</GroupLabel>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {(gallery.data ?? []).map((item) => {
                    const identity = identityOf(item.family);
                    const on = appliedId === item.id;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => applyTheme(item)}
                        disabled={busy !== null}
                        data-testid={`storefront-apply-${item.slug}`}
                        className="ed-theme"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          width: "100%",
                          padding: "9px 10px",
                          borderRadius: 12,
                          border: `1.5px solid ${on ? "var(--accent)" : "var(--hair)"}`,
                          background: on ? "var(--soft2)" : "var(--card)",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "start",
                        }}
                      >
                        {identity ? (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 34,
                              height: 34,
                              borderRadius: 10,
                              background: identity.accent,
                              color: identity.accentInk,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontFamily: identity.displayFont,
                              fontSize: 12,
                              fontWeight: 800,
                              flexShrink: 0,
                            }}
                          >
                            {identity.initial}
                          </span>
                        ) : null}
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span
                            style={{
                              display: "block",
                              fontSize: 12,
                              fontWeight: 800,
                              color: "var(--ink)",
                            }}
                          >
                            {nameOf(item)}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: 9.5,
                              fontWeight: 600,
                              color: "var(--ink3)",
                              marginTop: 1,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {bodyOf(item)}
                          </span>
                        </span>
                        {identity ? (
                          <span
                            aria-hidden="true"
                            style={{ display: "flex", gap: 3, flexShrink: 0 }}
                          >
                            {identity.swatches.map((swatch) => (
                              <span
                                key={swatch}
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: "50%",
                                  background: swatch,
                                  border: "1px solid rgba(0,0,0,.12)",
                                }}
                              />
                            ))}
                          </span>
                        ) : null}
                        {on ? (
                          <span
                            aria-hidden="true"
                            style={{
                              width: 15,
                              height: 15,
                              borderRadius: "50%",
                              background: "var(--accent)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <svg
                              width="9"
                              height="9"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="var(--accent-ink)"
                              strokeWidth="3.4"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M20 6 9 17l-5-5" />
                            </svg>
                          </span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {/*
                  Colours sit UNDER the theme rather than in a tab of their own:
                  they are that theme's palette, and the seller who just chose a
                  look is exactly the one who wants to nudge it.
                */}
                <GroupLabel style={{ margin: "18px 0 8px" }}>
                  {t((m) => m.manage.storefrontGroupColors)}
                </GroupLabel>
                <ThemeColorsEditor
                  key={Object.values(themeColors).join("-")}
                  colors={themeColors}
                  onChange={writeColors}
                  disabled={busy !== null}
                  compact
                />

                {/* The way back, at the FOOT of the tab that changed things —
                    quiet, because it throws away work, and reachable, because
                    a seller who has painted themselves into a corner needs it
                    more than they need it to be tidy. */}
                <button
                  type="button"
                  onClick={() => setResetOpen(true)}
                  disabled={!appliedId || busy !== null}
                  data-testid="storefront-reset"
                  className="tri-ib"
                  style={{
                    marginTop: 16,
                    width: "100%",
                    padding: "9px 10px",
                    borderRadius: 10,
                    border: "1px solid var(--hair)",
                    background: "transparent",
                    color: "var(--ink2)",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  {t((m) => m.manage.storefrontReset)}
                </button>
              </>
            ) : null}

            {!theme.isLoading && tab === "content" ? (
              /*
                The artboard fixes its groups — BRANDING, HERO, PROMO — because
                it draws one theme. Ours come from the SCHEMAS instead: every
                section that authors copy becomes a group, so a theme carrying a
                section the artboard never had still gets its words edited.
              */
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                {sections.map((section, index) => {
                  /*
                   * Two filters, and they answer different questions. The
                   * layout map says whether this SKIN reads the field at all;
                   * the page's own report says whether the DEVICE being
                   * previewed is currently showing it. Before the first report
                   * arrives only the first applies, so the panel is never
                   * momentarily empty.
                   */
                  const fields = copyFieldsFor(section, layout).filter(
                    (field) => {
                      if (!fieldReport) return true;
                      const pair = `${section.id}:${field.id}`;
                      if (!fieldReport.known.has(pair)) return true;
                      return fieldReport.visible.has(pair);
                    },
                  );
                  /*
                   * The switches, NOT filtered by what the page is showing: a
                   * banner that is currently off renders nothing, so the report
                   * cannot see it, and filtering on that would remove the one
                   * control that turns it back on.
                   */
                  const toggles = toggleFieldsFor(section, layout);
                  /* Section visibility lives in the Sections tab, which is
                     what that tab is for. A second switch here was the same
                     control in two places, and the one beside the words is the
                     one a seller would flip by accident. */
                  if (fields.length === 0 && toggles.length === 0) return null;
                  return (
                    <div key={section.id}>
                      <GroupLabel>{sectionLabel(section.type, locale)}</GroupLabel>

                      {toggles.map((toggle) => (
                        <label
                          key={toggle.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                            padding: "9px 11px",
                            borderRadius: 10,
                            background: "var(--soft2)",
                            border: "1px solid var(--hair)",
                            marginBottom: 9,
                            cursor: "pointer",
                          }}
                        >
                          <PillToggle
                            on={toggle.value}
                            disabled={busy !== null}
                            label={toggle.label[contentLocale]}
                            testId={`storefront-toggle-${section.id}-${toggle.id}`}
                            onToggle={() =>
                              writeSections(
                                writeToggle(
                                  sections,
                                  index,
                                  toggle.id,
                                  !toggle.value,
                                ),
                              )
                            }
                          />
                          <span style={{ fontSize: 11.5, fontWeight: 800 }}>
                            {toggle.label[contentLocale]}
                          </span>
                        </label>
                      ))}
                      <div
                        style={{ display: "flex", flexDirection: "column", gap: 9 }}
                      >
                        {fields.map((field) => {
                          const ar = readCopy(section, field.id, "ar");
                          const en = readCopy(section, field.id, "en");
                          return (
                            <Field
                              /*
                               * Keyed on the PREVIEW VERSION, never on the
                               * stored value.
                               *
                               * Keying on the value remounted the input every
                               * time a debounced save came back — the seller
                               * types, 800ms later the refetched theme arrives,
                               * the key changes, React throws the live input
                               * away and builds a new one, and the caret jumps
                               * to the end mid-word. The seller editor does not
                               * do that, and neither should this.
                               *
                               * `reseed` moves for exactly the changes that
                               * should refill the boxes — an undo, a reset, a
                               * different theme — and stays put both for the
                               * echo of the seller's own typing AND for the
                               * reload their typing sometimes causes.
                               */
                              key={`${section.id}-${field.id}-${reseed}`}
                              label={field.label[contentLocale]}
                              ar={ar}
                              en={en}
                              disabled={busy !== null}
                              testId={`storefront-copy-${section.id}-${field.id}`}
                              onChange={(fieldLocale, next) =>
                                writeSections(
                                  writeCopy(
                                    sections,
                                    index,
                                    field.id,
                                    fieldLocale,
                                    next,
                                  ),
                                  /*
                                   * The in-page patch is shallow: it writes
                                   * text, so a value carrying `[accent]`
                                   * markers lands without the styled segment
                                   * the skin would build. Those refresh once
                                   * typing settles, invisibly behind the double
                                   * buffer. So does a change to the language
                                   * the page is NOT showing, which the patch
                                   * cannot express at all.
                                   */
                                  {
                                    remount:
                                      /[[\]]/.test(next) ||
                                      fieldLocale !== contentLocale,
                                  },
                                )
                              }
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}

            {!theme.isLoading && tab === "sections" ? (
              <>
                <div
                  style={{ display: "flex", alignItems: "center", marginBottom: 8 }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: ".07em",
                      color: "var(--ink4)",
                    }}
                  >
                    {t((m) => m.manage.storefrontGroupSections)}
                  </span>
                  <span
                    style={{ fontSize: 9.5, fontWeight: 700, color: "var(--ink4)" }}
                  >
                    {t(
    countedKey(shownCount, {
      one: (m) => m.manage.storefrontSectionsVisibleOne,
      two: (m) => m.manage.storefrontSectionsVisibleTwo,
      few: (m) => m.manage.storefrontSectionsVisibleFew,
      many: (m) => m.manage.storefrontSectionsVisible,
    }),
    {
                      visible: shownCount,
                      total: sections.length,
                    })}
                  </span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {sections.map((section, index) => {
                    const locked = LOCKED_SECTION_TYPES.has(section.type);
                    const previewOnly = PREVIEW_ONLY_SECTION_TYPES.has(section.type);
                    const shown = isVisible(section);
                    return (
                      <div
                        key={section.id}
                        className="ed-row"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          padding: "8px 9px",
                          borderRadius: 10,
                          border: `1px solid ${
                            selected === section.id ? "var(--accent)" : "var(--hair)"
                          }`,
                          background: "var(--card)",
                          opacity: shown ? 1 : 0.5,
                        }}
                      >
                        {/* The ROW is the selector — a separate "edit" control
                            would be a second way to do what the row already
                            looks like it does. */}
                        <button
                          type="button"
                          onClick={() =>
                            setSelected((id) =>
                              id === section.id ? null : section.id,
                            )
                          }
                          aria-pressed={selected === section.id}
                          data-testid={`storefront-select-${section.id}`}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            border: "none",
                            background: "transparent",
                            padding: 0,
                            cursor: "pointer",
                            textAlign: "start",
                            fontFamily: "inherit",
                          }}
                        >
                          <span
                            style={{
                              display: "block",
                              fontSize: 11.5,
                              fontWeight: 800,
                              color: "var(--ink)",
                            }}
                          >
                            {sectionLabel(section.type, locale)}
                          </span>
                          <span
                            style={{
                              display: "block",
                              fontSize: 9,
                              fontWeight: 600,
                              color: "var(--ink4)",
                            }}
                          >
                            {previewOnly
                              ? t((m) => m.manage.storefrontPreviewOnly)
                              : locked
                                ? t((m) => m.manage.storefrontLocked)
                                : shown
                                  ? t((m) => m.manage.storefrontShow)
                                  : t((m) => m.manage.storefrontHide)}
                          </span>
                        </button>
                        <EditorIconButton
                          label={t((m) => m.manage.storefrontMoveUp)}
                          onClick={() =>
                            writeSections(moveSection(sections, index, -1))
                          }
                          disabled={!canMove(sections, index, -1) || busy !== null}
                          testId={`storefront-up-${section.id}`}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m18 15-6-6-6 6" />
                          </svg>
                        </EditorIconButton>
                        <EditorIconButton
                          label={t((m) => m.manage.storefrontMoveDown)}
                          onClick={() =>
                            writeSections(moveSection(sections, index, 1))
                          }
                          disabled={!canMove(sections, index, 1) || busy !== null}
                          testId={`storefront-down-${section.id}`}
                        >
                          <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="m6 9 6 6 6-6" />
                          </svg>
                        </EditorIconButton>
                        {/* The masthead, menu and footer are pinned by the
                            validator; an eye on them would offer a draft the
                            server rejects with an error nobody can act on. */}
                        {locked ? (
                          <span style={{ width: 24, flexShrink: 0 }} />
                        ) : (
                          <EditorIconButton
                            /* A state, not an action: the strings are
                               "Shown" / "Hidden", so pairing them with the
                               current value is the only reading that is true. */
                            label={`${sectionLabel(section.type, locale)} — ${
                              shown
                                ? t((m) => m.manage.storefrontShow)
                                : t((m) => m.manage.storefrontHide)
                            }`}
                            onClick={() =>
                              writeSections(toggleSection(sections, index))
                            }
                            disabled={busy !== null}
                            size={24}
                            testId={`storefront-visible-${section.id}`}
                          >
                            <EyeIcon on={shown} />
                          </EditorIconButton>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "#E9F0FE",
                    borderRadius: 10,
                    padding: "9px 11px",
                    marginTop: 12,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#2563EB",
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ flexShrink: 0 }}
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path d="M12 8h.01M12 11v5" />
                  </svg>
                  {t((m) => m.manage.storefrontMenuNote)}
                </div>
              </>
            ) : null}
          </div>
        </div>

        {/* ── CANVAS ─────────────────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            overflow: "hidden",
          }}
        >
          <DeviceFrame
            device={device}
            urlLabel={`${storeSlug}.trioty.shop`}
            pageColor={themeColors.surface}
            chromeBg={identityOf(layout)?.swatches[0] ?? "#1A1A19"}
            chromeFg="rgba(255,255,255,.55)"
          >
            <StorefrontPreview
              storeSlug={storeSlug}
              locale={locale}
              token={preview.isError ? null : (preview.data?.token ?? "")}
              version={previewVersion}
              device={device}
              selectedSectionId={selected}
              onSelect={(sectionId) => {
                setSelected(sectionId);
                // Clicking the page means "change THIS", so land on the words.
                setTab("content");
              }}
              onUnpatched={handleUnpatched}
              onFields={(report) =>
                setFieldReport({
                  visible: new Set(report.fields),
                  known: new Set(report.known),
                })
              }
              livePatch={livePatch}
            />
          </DeviceFrame>
        </div>
      </div>

      {/*
        UX-round D12 — ONE CONFIRM PRIMITIVE IN THE PRODUCT, adopted one site at a time.
        «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
        This was `apps/restaurant`'s last `ConfirmDialog`; every other
        confirmation in this app is already a `ConfirmModal`, so the two
        primitives were rendering two different dialogs — a compact centred
        card here, the platform's header/body/footer chrome everywhere else —
        for the same kind of question.

        `tone="danger"` is the DEFAULT and is also what the three-question test
        produces: Q1 is no, because resetting to the theme drops the draft and
        nothing on this screen puts it back. It is therefore the same red the
        `ConfirmDialog` rendered, and this conversion re-tones nothing.

        `StorefrontEditorPage.reset.spec.tsx` pins it: the prompt appears on
        the reset control, the confirm is the one that calls the endpoint, and
        the cancel leaves the draft alone.
      */}
      <ConfirmModal
        isOpen={resetOpen}
        tone="danger"
        title={t((m) => m.manage.storefrontResetTitle)}
        description={t((m) => m.manage.storefrontResetBody)}
        confirmLabel={t((m) => m.manage.storefrontResetConfirm)}
        cancelLabel={t((m) => m.common.cancel)}
        closeLabel={t((m) => m.common.close)}
        isSubmitting={busy === "reset"}
        onConfirm={resetToTheme}
        onClose={() => setResetOpen(false)}
        testId="storefront-reset-prompt"
      />
    </div>
  );
}
